import React, { useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductCard } from '../components/products/ProductCard';
import { ProductComparison } from '../components/products/ProductComparison';
import { Button } from '../components/ui/Button';
interface ProductsPageProps {
  onRequestQuote: () => void;
}
export function ProductsPage({
  onRequestQuote
}: ProductsPageProps) {
  const [offset, setOffset] = useState(0);
  // On desktop show 3 cards, on mobile 1
  // We'll use CSS to handle responsive, but control offset in JS
  const maxOffset = products.length - 1;
  const prev = () => setOffset(o => Math.max(0, o - 1));
  const next = () => setOffset(o => Math.min(maxOffset, o + 1));
  return <main className="min-h-screen bg-dark-bg" data-id="element-787">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-788">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-789">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-790">
            D-TECH <span className="text-brand-yellow" data-id="element-791">proizvodi</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-792">
            Tri serije — stepenišni transporteri i industrijska kolica. Svaki
            model projektovan za specifičan tip rada.
          </p>
        </div>
      </section>

      {/* Product Carousel */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border" data-id="element-793">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-794">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-795">
            Izaberite <span className="text-brand-yellow" data-id="element-796">model</span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-797">
            SC-A i SC-B su stepenišni transporteri. SC-C su električna kolica za
            ravne površine.
          </p>

          {/* Carousel wrapper */}
          <div className="relative" data-id="element-798">
            {/* Left arrow */}
            <button onClick={prev} disabled={offset === 0} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow disabled:opacity-20 disabled:cursor-not-allowed transition-colors shadow-lg" aria-label="Prethodni" data-id="element-799">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-800" />
            </button>

            {/* Sliding track */}
            <div className="overflow-hidden" data-id="element-801">
              <div className="flex gap-6 transition-transform duration-400 ease-in-out" style={{
              transform: `translateX(calc(-${offset} * (100% / 3 + 8px)))`
            }} data-id="element-802">
                {products.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]" data-id="element-803">
                    <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} data-id="element-804" />
                  </div>)}
              </div>
            </div>

            {/* Right arrow */}
            <button onClick={next} disabled={offset >= maxOffset} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow disabled:opacity-20 disabled:cursor-not-allowed transition-colors shadow-lg" aria-label="Sledeći" data-id="element-805">
              <ChevronRightIcon className="w-5 h-5" data-id="element-806" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-807">
            {products.map((p, idx) => <button key={p.id} onClick={() => setOffset(idx)} className={`transition-all rounded-full ${idx === offset ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={p.name} data-id="element-808" />)}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border bg-dark-surface" data-id="element-809">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-810">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-811">
            Uporedi <span className="text-brand-yellow" data-id="element-812">sve modele</span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-813">
            SC-A i SC-B su stepenišni transporteri. SC-C su električna kolica za
            ravne površine.
          </p>
          <ProductComparison data-id="element-814" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-dark-surface" data-id="element-815">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-816">
          <h2 className="text-3xl font-black text-white mb-4" data-id="element-817">
            Niste sigurni koji model?
          </h2>
          <p className="text-gray-400 mb-8" data-id="element-818">
            Kontaktirajte nas — pomoći ćemo vam da izaberete pravo rešenje za
            vaše uslove rada.
          </p>
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-819">
            Zatražite ponudu
          </Button>
        </div>
      </section>
    </main>;
}